import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Filter, AlertTriangle, Cog, X, ChevronDown, Pencil, Trash2 } from "lucide-react";
import ImportButton from "@/components/ImportButton";
import ExportXlsxButton from "@/components/ExportXlsxButton";
import { PageShell } from "@/components/SidebarLayout";
import { api, formatApiError } from "@/lib/api";
import { formatPct, monthLabel } from "@/lib/format";
import { TEST_IDS } from "@/testIds";

export default function Milestones() {
  const [rows, setRows] = useState([]);
  const [projects, setProjects] = useState([]);
  const [master, setMaster] = useState([]);
  const [completedPct, setCompletedPct] = useState({}); // project -> {completion_pct, as_on_month}
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({});
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("");
  const [projectFilter, setProjectFilter] = useState("all");
  const [showPaymentPlan, setShowPaymentPlan] = useState(null); // project object when opened
  const [showLandConfig, setShowLandConfig] = useState(null);
  const [overAck, setOverAck] = useState(false); // user has seen the inline over-cap warning and may "Save anyway"

  const load = useCallback(async () => {
    const [r, p, m, op] = await Promise.all([
      api.get("/milestones"), // now excludes standard rows by default
      api.get("/projects"),
      api.get("/milestone-master"),
      api.get("/settings/opening-position").catch(() => ({ data: { rows: [] } })),
    ]);
    setRows(r.data);
    setProjects(p.data);
    setMaster(m.data.filter((x) => !x.is_standard));
    const cp = {};
    for (const row of op.data?.rows || []) {
      if (row.completion_pct != null) cp[row.project] = { completion_pct: row.completion_pct, as_on_month: op.data.as_on_month };
    }
    setCompletedPct(cp);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => rows.filter((r) => {
    if (projectFilter !== "all" && r.project_name !== projectFilter) return false;
    if (!filter.trim()) return true;
    const q = filter.toLowerCase();
    return (r.project_name || "").toLowerCase().includes(q) || (r.name || "").toLowerCase().includes(q);
  }), [rows, filter, projectFilter]);

  // Sum of custom milestone % per project (used to warn if crossing (1 - standardTotal))
  // Pre-system rows (Historical/Pending) are one-time absolute back-fills,
  // not % stage billing — they must not count against the cap.
  const isPreSystem = (name) => (name || "").includes("Pre-System");
  const projectPctSum = useMemo(() => {
    const acc = {};
    for (const r of rows) {
      if (isPreSystem(r.name)) continue;
      acc[r.project_name] = (acc[r.project_name] || 0) + (r.milestone_pct || 0);
    }
    return acc;
  }, [rows]);

  const startCreate = () => { setDraft({ collection_lag_months: 1, milestone_pct: 0.075 }); setCreating(true); setEditingId(null); setOverAck(false); };
  const startEdit = (r) => { setDraft({ ...r }); setEditingId(r.id); setCreating(false); setOverAck(false); };
  const cancel = () => { setDraft({}); setEditingId(null); setCreating(false); setOverAck(false); };

  const resolve = () => {
    const proj = projects.find((p) => p.name === draft.project_name);
    return {
      project_id: proj?.id || draft.project_id || "",
      project_name: draft.project_name,
      name: draft.name || "Milestone",
      demand_month: draft.demand_month,
      milestone_pct: Number(draft.milestone_pct) || 0,
      collection_lag_months: Number.isFinite(Number(draft.collection_lag_months)) ? Number(draft.collection_lag_months) : 1,
    };
  };

  // Warn if committing this milestone will push the project's custom-milestone sum over the allowed cap.
  // Assume 60% cap by default (i.e. standard 10 + 30 = 40%). We'll refine with per-project rule below.
  const willOverflow = () => {
    const body = resolve();
    if (!body.project_name) return null;
    if (isPreSystem(body.name)) return null; // back-fill rows don't count against the cap
    const projRow = projects.find((p) => p.name === body.project_name);
    const globalCap = 1 - 0.10 - 0.20;
    const pp = projRow?.payment_plan_override || null;
    const bookingPct = pp?.booking_pct ?? 0.10;
    const nextPct = pp?.next_pct ?? 0.20;
    const cap = 1 - bookingPct - nextPct;
    const currentSum = projectPctSum[body.project_name] || 0;
    const existing = editingId ? (rows.find((r) => r.id === editingId)?.milestone_pct || 0) : 0;
    const newSum = currentSum - existing + body.milestone_pct;
    const remaining = cap - newSum;
    return { cap, newSum, remaining, over: remaining < -0.001, globalCap };
  };

  // Over-cap rule applies only when the milestone % itself is being set or
  // changed — editing lag, name or demand month alone never triggers it.
  const isPctTouched = () => creating
    || Math.abs(Number(draft.milestone_pct ?? 0) - Number(rows.find((r) => r.id === editingId)?.milestone_pct ?? 0)) > 1e-9;

  const save = async (forceOverride = false) => {
    let body;
    try {
      body = resolve();
    } catch (e) {
      return toast.error("Could not read the form values", { description: String(e?.message || e) });
    }
    if (!body.project_id) return toast.error("Pick a project");
    if (!body.demand_month) return toast.error("Pick a demand month");
    const check = isPctTouched() ? willOverflow() : null;
    if (check?.over && !forceOverride) {
      // No blocking native dialog — flag it inline; the form's Save button
      // becomes "Save anyway" and a second click commits. Finance inputs
      // override the Excel-derived plan.
      setOverAck(true);
      return;
    }
    setBusy(true);
    try {
      // Add the name to Milestone Master if it's brand new (not standard, not already there)
      if (creating && body.name && !master.some((m) => m.name.toLowerCase() === body.name.toLowerCase())) {
        try {
          await api.post("/milestone-master", {
            name: body.name,
            default_pct: body.milestone_pct,
            default_lag_months: body.collection_lag_months,
          });
        } catch { /* non-fatal */ }
      }
      if (creating) await api.post("/milestones", body);
      else await api.put(`/milestones/${editingId}`, body);
      toast.success("Saved");
      cancel();
      await load();
    } catch (e) {
      toast.error("Save failed", { description: formatApiError(e.response?.data?.detail) });
    } finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this milestone?")) return;
    try {
      await api.delete(`/milestones/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error("Delete failed", { description: formatApiError(e.response?.data?.detail) });
    }
  };

  const overflow = willOverflow();
  const overActive = Boolean(overflow?.over && isPctTouched());
  const saveLabel = overActive && overAck ? "Save anyway" : undefined;

  return (
    <PageShell testId="milestones-page" kicker="Construction Milestones" title="Milestones" hideProjectFilter actions={
      <div className="flex items-center gap-2">
        <ExportXlsxButton
          endpoint="/exports/milestones.xlsx"
          filename="milestones.xlsx"
          params={projectFilter !== "all" ? { project: projectFilter } : {}}
          testId="milestones-export"
          label="Export Excel"
        />
        <ImportButton testId="milestones-import" onDone={load} />
      </div>
    }>
      <p className="-mt-2 text-muted-dark text-sm max-w-3xl">
        Custom milestones per project. The 10% Booking + Next-Month rates are per-project — configure them
        globally in{" "}
        <a href="/settings" className="text-ink underline underline-offset-2 hover:no-underline">Settings</a>{" "}
        or override per project below.
      </p>

      <div className="glass-card overflow-hidden">
        <div className="flex flex-wrap items-center gap-3 px-5 py-3.5">
          <div className="hidden md:flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-muted-light font-semibold">
            <Filter size={11} /> Filter
          </div>
          <select
            data-testid="milestones-project-filter"
            value={projectFilter}
            onChange={(e) => setProjectFilter(e.target.value)}
            className="cf-touch px-3 py-2 bg-surface/60 rounded-lg text-sm text-ink focus:outline-none focus:ring-2 focus:ring-indigo-300"
          >
            <option value="all">All Projects</option>
            {projects.map((p) => (
              <option key={p.id} value={p.name}>{p.name}{p.type === "land" ? " · land" : ""}</option>
            ))}
          </select>
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search milestone name…"
            data-testid={TEST_IDS.searchInput("milestones")}
            className="cf-touch flex-1 min-w-[180px] max-w-sm px-3 py-2 text-sm bg-surface/60 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-300"
          />
          <span className="text-xs text-muted-light num ml-auto">{filtered.length} rows</span>
          {projectFilter !== "all" && (() => {
            const proj = projects.find((p) => p.name === projectFilter);
            if (proj?.type === "land") {
              return (
                <button
                  data-testid="milestones-land-config-btn"
                  onClick={() => setShowLandConfig(proj)}
                  className="cf-touch inline-flex items-center gap-1.5 px-3.5 py-2 text-sm rounded-full bg-surface/70 border border-white/70 text-ink"
                  title={`Land split for ${projectFilter}`}
                >
                  <Cog size={13} /> Land split
                </button>
              );
            }
            return (
              <button
                onClick={() => setShowPaymentPlan(proj)}
                className="cf-touch inline-flex items-center gap-1.5 px-3.5 py-2 text-sm rounded-full bg-surface/70 border border-white/70 text-ink"
                title={`Payment plan for ${projectFilter}`}
              >
                <Cog size={13} /> Payment plan
              </button>
            );
          })()}
          <button
            data-testid={TEST_IDS.addRow("milestones")}
            onClick={startCreate}
            disabled={creating || editingId}
            className="cf-touch inline-flex items-center gap-1.5 px-3.5 py-2 text-sm rounded-full bg-ink text-white disabled:opacity-50"
            style={{ boxShadow: "0 4px 12px rgba(18,49,89,0.15)" }}
          >
            + Add Custom
          </button>
        </div>

        {overActive && (creating || editingId) && (
          <div data-testid="milestones-overcap-banner" className="mx-5 mb-3 flex items-start gap-2 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-red-700 text-xs">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <div>
              <strong>Over cap.</strong> {draft.project_name}&apos;s custom milestones would sum to {(overflow.newSum * 100).toFixed(1)}%.
              Allowed cap after standard payment plan is {(overflow.cap * 100).toFixed(1)}%.
              Remaining: <span className="num">{(overflow.remaining * 100).toFixed(1)}%</span>.
              {overAck && (
                <div className="mt-1 font-semibold text-ink">Your inputs override the Excel-derived plan — click <span className="underline">Save anyway</span> in the form to confirm.</div>
              )}
            </div>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full min-w-[860px]">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-muted-light border-b border-white/60 bg-surface/40">
                <th className="sticky left-0 z-10 bg-surface text-left font-semibold px-4 py-3 whitespace-nowrap" style={{ minWidth: 160 }}>Project</th>
                <th className="text-left font-semibold px-4 py-3 whitespace-nowrap">Milestone</th>
                <th className="text-left font-semibold px-4 py-3 whitespace-nowrap">Demand Month</th>
                <th className="text-right font-semibold px-4 py-3 whitespace-nowrap">%</th>
                <th className="text-right font-semibold px-4 py-3 whitespace-nowrap">Lag</th>
                <th className="text-right font-semibold px-4 py-3 whitespace-nowrap">Actions</th>
              </tr>
            </thead>
            <tbody>
              {creating && (
                <NewOrEditRow draft={draft} setDraft={setDraft} projects={projects} master={master} onSave={() => save(overAck)} onCancel={cancel} busy={busy} saveLabel={saveLabel} />
              )}
              {projectFilter !== "all" && completedPct[projectFilter] && (
                <tr data-testid="milestones-completed-row" className="border-b border-white/50 bg-emerald-50/70">
                  <td className="sticky left-0 z-10 px-4 py-2 text-sm text-ink font-medium" style={{ minWidth: 160, background: "var(--cf-surface)" }}>{projectFilter}</td>
                  <td className="px-4 py-2 text-sm text-ink">
                    Completed as of {monthLabel(completedPct[projectFilter].as_on_month)}
                    <span className="ml-2 text-[10px] uppercase tracking-widest text-muted-light">from Settings</span>
                  </td>
                  <td className="px-4 py-2 text-sm num text-muted-dark">{monthLabel(completedPct[projectFilter].as_on_month)}</td>
                  <td className="px-4 py-2 text-right num text-sm text-ink font-semibold">{formatPct(completedPct[projectFilter].completion_pct / 100)}</td>
                  <td className="px-4 py-2 text-right num text-sm text-muted-dark">—</td>
                  <td className="px-4 py-2 text-right text-[11px] text-muted-light whitespace-nowrap">read-only · edit in Settings</td>
                </tr>
              )}
              {filtered.map((r, i) => {
                const isEditing = editingId === r.id;
                if (isEditing) return <tr key={r.id}><td colSpan={6} className="p-0"><table className="w-full"><tbody><NewOrEditRow draft={draft} setDraft={setDraft} projects={projects} master={master} onSave={() => save(overAck)} onCancel={cancel} busy={busy} saveLabel={saveLabel} /></tbody></table></td></tr>;
                const rowBg = i % 2 === 0 ? "var(--cf-surface)" : "var(--cf-surface-2)";
                return (
                  <tr key={r.id} className={`border-b border-white/50 last:border-0 hover:bg-surface/50`}>
                    <td className="sticky left-0 z-10 px-4 py-2 text-sm text-ink font-medium" style={{ minWidth: 160, background: rowBg }}>{r.project_name}</td>
                    <td className="px-4 py-2 text-sm text-ink">{r.name}</td>
                    <td className="px-4 py-2 text-sm num text-muted-dark">{monthLabel(r.demand_month)}</td>
                    <td className="px-4 py-2 text-right num text-sm text-ink">{formatPct(r.milestone_pct)}</td>
                    <td className="px-4 py-2 text-right num text-sm text-muted-dark">{r.collection_lag_months ?? "—"}</td>
                    <td className="px-4 py-2 text-right whitespace-nowrap">
                      <div className="inline-flex items-center gap-1">
                        <button
                          data-testid={TEST_IDS.editRow("milestones", r.id)}
                          onClick={() => startEdit(r)}
                          disabled={creating || editingId}
                          title="Edit"
                          className="p-1.5 rounded-md text-muted-dark hover:text-ink hover:bg-surface-2 disabled:opacity-30 transition-colors"
                        ><Pencil size={13} strokeWidth={2} /></button>
                        <button
                          data-testid={TEST_IDS.deleteRow("milestones", r.id)}
                          onClick={() => del(r.id)}
                          disabled={creating || editingId}
                          title="Delete"
                          className="p-1.5 rounded-md text-muted-dark hover:text-red-600 hover:bg-red-50 disabled:opacity-30 transition-colors"
                        ><Trash2 size={13} strokeWidth={2} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && !creating && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-light text-sm">No milestones match your filter.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showPaymentPlan && (
        <PaymentPlanDrawer project={showPaymentPlan} onClose={() => setShowPaymentPlan(null)} onSaved={load} />
      )}
      {showLandConfig && (
        <LandConfigDrawer project={showLandConfig} onClose={() => setShowLandConfig(null)} onSaved={load} />
      )}
    </PageShell>
  );
}

function NewOrEditRow({ draft, setDraft, projects, master, onSave, onCancel, busy, saveLabel }) {
  const [nameOpen, setNameOpen] = useState(false);
  const [nameSearch, setNameSearch] = useState(draft.name || "");
  useEffect(() => { setNameSearch(draft.name || ""); }, [draft.id, draft.name]);
  const filteredMaster = master.filter((m) => m.name.toLowerCase().includes(nameSearch.toLowerCase()));
  const pickMaster = (m) => {
    setDraft({ ...draft, name: m.name, milestone_pct: m.default_pct ?? draft.milestone_pct, collection_lag_months: m.default_lag_months ?? draft.collection_lag_months });
    setNameSearch(m.name);
    setNameOpen(false);
  };
  return (
    <tr className="bg-amber-50/60 border-b border-white/60">
      <td className="px-3 py-2">
        <select
          data-testid="milestones-project-select"
          value={draft.project_name || ""}
          onChange={(e) => setDraft({ ...draft, project_name: e.target.value })}
          className="cf-touch w-full px-2 py-2 bg-surface rounded-lg text-sm"
        >
          <option value="">—</option>
          {projects.filter((p) => p.type !== "land").map((p) => <option key={p.id} value={p.name}>{p.name}</option>)}
        </select>
      </td>
      <td className="px-3 py-2 relative">
        <div className="flex items-center gap-1">
          <input
            data-testid="milestones-name-input"
            value={nameSearch}
            onChange={(e) => { setNameSearch(e.target.value); setDraft({ ...draft, name: e.target.value }); setNameOpen(true); }}
            onFocus={() => setNameOpen(true)}
            placeholder="Pick from master or type new…"
            className="cf-touch flex-1 px-2 py-2 bg-surface rounded-lg text-sm"
          />
          <button onClick={() => setNameOpen((o) => !o)} className="p-1.5 text-muted-dark hover:text-ink"><ChevronDown size={14} /></button>
        </div>
        {nameOpen && (
          <div className="absolute z-20 mt-1 left-3 right-3 max-h-60 overflow-y-auto glass-card p-1 shadow-xl">
            {filteredMaster.slice(0, 40).map((m) => (
              <button key={m.id} onClick={() => pickMaster(m)} className="w-full text-left px-3 py-1.5 text-xs hover:bg-surface rounded-md flex items-center justify-between">
                <span className="truncate">{m.name}</span>
                {m.default_pct != null && <span className="num text-muted-light text-[10px]">{formatPct(m.default_pct)}</span>}
              </button>
            ))}
            {nameSearch && !master.some((m) => m.name.toLowerCase() === nameSearch.toLowerCase()) && (
              <div className="px-3 py-1.5 text-[11px] text-muted-light border-t border-white/60 mt-1">
                &ldquo;{nameSearch}&rdquo; is new — will be added to Milestone Master on save.
              </div>
            )}
          </div>
        )}
      </td>
      <td className="px-3 py-2">
        <input
          data-testid="milestones-demand-month"
          type="month"
          value={(draft.demand_month || "").slice(0, 7)}
          onChange={(e) => setDraft({ ...draft, demand_month: e.target.value + "-01" })}
          className="cf-touch w-full px-2 py-2 bg-surface rounded-lg text-sm num"
        />
      </td>
      <td className="px-3 py-2">
        <input
          data-testid="milestones-pct-input"
          type="number"
          step="0.001"
          value={draft.milestone_pct ?? 0}
          onChange={(e) => setDraft({ ...draft, milestone_pct: parseFloat(e.target.value) || 0 })}
          className="cf-touch w-full px-2 py-2 bg-surface rounded-lg text-sm num text-right"
          placeholder="0.075"
        />
      </td>
      <td className="px-3 py-2">
        <input
          type="number"
          value={draft.collection_lag_months ?? 1}
          onChange={(e) => setDraft({ ...draft, collection_lag_months: parseInt(e.target.value) || 0 })}
          className="cf-touch w-full px-2 py-2 bg-surface rounded-lg text-sm num text-right"
        />
      </td>
      <td className="px-3 py-2 text-right space-x-1 whitespace-nowrap">
        <button
          data-testid={TEST_IDS.saveRow("milestones")}
          onClick={onSave}
          disabled={busy}
          className={`px-3 py-1.5 text-xs rounded-md text-white disabled:opacity-50 ${saveLabel ? "bg-amber-600 hover:bg-amber-700" : "bg-ink"}`}
        >
          {busy ? "Saving…" : (saveLabel || "Save")}
        </button>
        <button onClick={onCancel} className="px-3 py-1.5 text-xs rounded-md text-muted-dark">Cancel</button>
      </td>
    </tr>
  );
}

function PaymentPlanDrawer({ project, onClose, onSaved }) {
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/projects/${project.id}/payment-plan`).then(({ data }) => setPlan(data));
  }, [project.id]);

  const save = async (override) => {
    setBusy(true);
    try {
      await api.put(`/projects/${project.id}/payment-plan`, override);
      toast.success("Payment plan updated · projections re-run");
      onSaved();
      onClose();
    } catch (e) {
      toast.error("Save failed", { description: formatApiError(e.response?.data?.detail) });
    } finally { setBusy(false); }
  };

  const clear = () => save({ booking_pct: null, next_pct: null, next_lag_months: null });

  if (!plan) return null;
  const eff = plan.effective;

  return (
    <div className="fixed inset-0 z-50 bg-black/30 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="glass-card max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-light">Per-project override</div>
            <h2 className="font-display text-xl text-ink">{project.name} · Payment plan</h2>
          </div>
          <button onClick={onClose} className="p-2 text-muted-dark hover:text-ink"><X size={16} /></button>
        </div>
        <p className="text-xs text-muted-dark mb-4">
          Global defaults: <span className="num">{(plan.global_default.booking_pct * 100).toFixed(1)}%</span> booking,{" "}
          <span className="num">{(plan.global_default.next_pct * 100).toFixed(1)}%</span> next-month (lag {plan.global_default.next_lag_months}).
          Leave a field blank to inherit.
        </p>
        <FormRow project={project} eff={eff} onSave={save} onClear={clear} onClose={onClose} hasOverride={plan.has_override} busy={busy} />
      </div>
    </div>
  );
}

function FormRow({ eff, onSave, onClear, onClose, hasOverride, busy }) {
  const [booking, setBooking] = useState(eff.booking_pct);
  const [next, setNext] = useState(eff.next_pct);
  const [lag, setLag] = useState(eff.next_lag_months);
  return (
    <>
      <div className="grid grid-cols-3 gap-3">
        <label className="text-xs">
          <span className="block text-muted-dark mb-1">Booking %</span>
          <input type="number" step="0.001" value={booking} onChange={(e) => setBooking(parseFloat(e.target.value))} className="w-full px-2 py-2 bg-surface rounded-lg num text-right" />
        </label>
        <label className="text-xs">
          <span className="block text-muted-dark mb-1">Next month %</span>
          <input type="number" step="0.001" value={next} onChange={(e) => setNext(parseFloat(e.target.value))} className="w-full px-2 py-2 bg-surface rounded-lg num text-right" />
        </label>
        <label className="text-xs">
          <span className="block text-muted-dark mb-1">Next-month lag</span>
          <input type="number" value={lag} onChange={(e) => setLag(parseInt(e.target.value))} className="w-full px-2 py-2 bg-surface rounded-lg num text-right" />
        </label>
      </div>
      <div className="text-[11px] text-muted-light mt-2">
        Combined: <span className="num">{((booking + next) * 100).toFixed(1)}%</span> sales-linked (remaining{" "}
        <span className="num">{((1 - booking - next) * 100).toFixed(1)}%</span> collected via milestones).
      </div>
      <div className="mt-5 flex items-center justify-between">
        {hasOverride ? (
          <button onClick={onClear} disabled={busy} className="text-xs text-red-600 hover:underline">Clear override, use global</button>
        ) : <span />}
        <div className="flex items-center gap-2">
          <button data-testid="payment-plan-cancel" onClick={onClose} className="px-3 py-2 text-xs text-muted-dark">Cancel</button>
          <button
            onClick={() => onSave({ booking_pct: booking, next_pct: next, next_lag_months: lag })}
            disabled={busy}
            className="px-4 py-2 text-sm rounded-full bg-ink text-white disabled:opacity-50"
          >{busy ? "Saving…" : "Save override"}</button>
        </div>
      </div>
    </>
  );
}

function LandConfigDrawer({ project, onClose, onSaved }) {
  const [cfg, setCfg] = useState(null);
  const [pctInput, setPctInput] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/projects/${project.id}/land-config`).then(({ data }) => {
      setCfg(data);
      setPctInput(((data.same_month_pct ?? 0.10) * 100).toFixed(2));
    });
  }, [project.id]);

  const save = async () => {
    const pct = parseFloat(pctInput);
    if (Number.isNaN(pct) || pct < 0 || pct > 100) {
      toast.error("Same-month % must be between 0 and 100");
      return;
    }
    setBusy(true);
    try {
      await api.put(`/projects/${project.id}/land-config`, { same_month_pct: pct / 100 });
      toast.success("Land split updated · Collections re-run");
      onSaved();
      onClose();
    } catch (e) {
      toast.error("Save failed", { description: formatApiError(e.response?.data?.detail) });
    } finally { setBusy(false); }
  };

  if (!cfg) return null;
  const pct = parseFloat(pctInput) || 0;
  return (
    <div className="fixed inset-0 z-50 bg-black/30 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="glass-card max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-light">Land project · collection timing</div>
            <h2 className="font-display text-xl text-ink">{project.name} · Land split</h2>
          </div>
          <button onClick={onClose} className="p-2 text-muted-dark hover:text-ink"><X size={16} /></button>
        </div>
        <p className="text-xs text-muted-dark mb-4">
          Development fee (15% of plot sale value) is split across two months:{" "}
          <b>{pct.toFixed(0)}% at sale month</b>, balance <b>{(100 - pct).toFixed(0)}%</b> collected{" "}
          <b>60 days later</b>. Balance lag is a fixed 2-month policy.
        </p>
        <label className="block text-xs mb-2">
          <span className="block text-muted-dark mb-1">Same-month % (0–100)</span>
          <input
            data-testid="land-config-same-pct"
            type="number" step="0.5" min="0" max="100"
            value={pctInput}
            onChange={(e) => setPctInput(e.target.value)}
            className="w-full px-2 py-2 bg-surface rounded-lg num text-right"
          />
        </label>
        <div className="text-[11px] text-muted-light mb-4">
          Example: sale ₹5.20 Cr → fee ₹78 L → <span className="num">₹{(78 * pct / 100).toFixed(1)} L</span> same month,{" "}
          <span className="num">₹{(78 * (100 - pct) / 100).toFixed(1)} L</span> in 60 days.
        </div>
        <div className="flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-3 py-2 text-xs text-muted-dark">Cancel</button>
          <button
            data-testid="land-config-save"
            onClick={save} disabled={busy}
            className="px-4 py-2 text-sm rounded-full bg-ink text-white disabled:opacity-50"
          >{busy ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
}
